<template>
    <div :class="['inline-edit-wrapper', { dark: darkScheme }]">
        <div class="inline-edit-input" :class="{ editable: isEditing, 'always-editable': alwaysEditable, focused: isInputFocused, disabled, error: !!validationError }" ref="container">
            <div class="input-wrapper" @mousedown="preventFocusedInputBlur" @click="focusInput">
                <Icon v-if="leadingIcon" :icon-name="leadingIcon" class="leading-icon" />
                <span v-if="hasTextOverflow" class="overflow-marker" />
                <div ref="textViewport" class="text-viewport">
                    <div class="text-track">
                        <span v-if="prefixText" class="prefix-text">{{ prefixText }}</span>
                        <input
                            ref="input"
                            v-model="inputValue"
                            :readonly="(!isEditing && !alwaysEditable) || isSaving"
                            :disabled="disabled"
                            :type="inputType"
                            :maxlength="maxLength"
                            :size="alwaysEditable && prefixText ? Math.max(inputValue.length, 1) : null"
                            @focus="handleFocus"
                            @blur="handleBlur"
                            @input="handleInput"
                            @keydown.enter.prevent="showActionButton && toggleAction"
                        />
                    </div>
                </div>
            </div>
            <CtaButton v-if="showActionButton" class="action-button" :dark-scheme="darkScheme" size="tiny" :disabled="disabled || isSaving" type="icon subtle" @click="toggleAction">
                <Icon :icon-name="trailingIcon" />
            </CtaButton>
        </div>
        <div v-if="showCharLimit" :class="['char-limit-message', charLimitPosition, { 'limit-reached': currentLength >= maxLength }]">
            {{ currentLength }} / {{ maxLength }}
        </div>
        <div v-if="validationError" class="assist-text">{{ validationError }}</div>
    </div>
</template>

<script>
import Icon from "@/scripts/common/Icon.vue";
import CtaButton from "@/scripts/common/components/CtaButton.vue";

export default {
    name: "InlineEditInput",
    components: {
        CtaButton,
        Icon,
    },
    props: {
        modelValue: {
            type: String,
            default: "",
        },
        saveHandler: {
            type: Function,
            default: null,
        },
        validateHandler: {
            type: Function,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        inputType: {
            type: String,
            default: "text",
        },
        maxLength: {
            type: Number,
            default: 254,
        },
        charLimitPosition: {
            type: String,
            default: null, // inside | outside
        },
        darkScheme: {
            type: Boolean,
            default: false,
        },
        leadingIcon: {
            type: String,
            default: null,
        },
        prefixText: {
            type: String,
            default: "",
        },
        showActionButton: {
            type: Boolean,
            default: true,
        },
        alwaysEditable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            isEditing: false,
            isSaving: false,
            inputValue: "",
            originalValue: "",
            validationError: "",
            isInputFocused: false,
            hasTextOverflow: false,
        };
    },
    computed: {
        trailingIcon() {
            return this.isEditing ? "check" : "edit";
        },
        currentLength() {
            return String(this.inputValue ?? this.modelValue ?? "").length;
        },
        showCharLimit() {
            if (this.charLimitPosition !== "outside" || !this.maxLength) {
                return false;
            }

            return this.currentLength >= Math.max(Math.ceil(this.maxLength * 0.8), this.maxLength - 10);
        },
    },
    watch: {
        modelValue(newValue) {
            if (!this.isEditing) {
                this.inputValue = newValue || "";
                this.originalValue = newValue || "";
            }
        },
    },
    mounted() {
        this.inputValue = this.modelValue || "";
        this.originalValue = this.modelValue || "";
        document.addEventListener("click", this.handleOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleOutsideClick);
    },
    methods: {
        preventFocusedInputBlur(event) {
            if (this.alwaysEditable && this.isInputFocused && event.target !== this.$refs.input) {
                event.preventDefault();
            }
        },
        focusInput() {
            if (!this.alwaysEditable || this.isInputFocused || this.disabled || this.isSaving) return;
            this.$refs.input?.focus();
        },
        handleFocus() {
            this.isInputFocused = true;
            this.scrollTextToEnd();
        },
        handleBlur() {
            this.isInputFocused = false;
            this.hasTextOverflow = false;
            const viewport = this.$refs.textViewport;
            if (viewport) {
                viewport.scrollLeft = 0;
            }
        },
        handleInput() {
            if (this.alwaysEditable) {
                this.$emit("update:modelValue", this.inputValue);
            }
            this.scrollTextToEnd();
        },
        scrollTextToEnd() {
            if (!this.alwaysEditable || !this.prefixText || !this.isInputFocused) return;

            this.$nextTick(() => {
                const viewport = this.$refs.textViewport;
                const input = this.$refs.input;
                if (!viewport) return;

                if (input) {
                    input.scrollLeft = 0;
                }

                const hasOverflow = viewport.scrollWidth > viewport.clientWidth;
                this.hasTextOverflow = hasOverflow;
                viewport.scrollLeft = hasOverflow ? viewport.scrollWidth : 0;
            });
        },
        handleOutsideClick(event) {
            if (!this.isEditing) return;
            if (!this.$refs.container?.contains(event.target)) {
                this.revertChanges();
            }
        },
        startEditing() {
            if (this.disabled || this.isSaving) return;
            this.isEditing = true;
            this.validationError = "";
            this.originalValue = this.modelValue || "";
            this.inputValue = this.originalValue;
            this.$nextTick(() => {
                this.$refs.input?.focus();
                this.$refs.input?.select?.();
            });
        },
        revertChanges() {
            this.inputValue = this.originalValue;
            this.isEditing = false;
            this.validationError = "";
        },
        toggleAction() {
            if (this.isEditing) {
                this.saveChanges();
            } else {
                this.startEditing();
            }
        },
        validateValue(value) {
            if (!this.validateHandler) {
                this.validationError = "";
                return true;
            }

            const result = this.validateHandler(value);
            if (result === true || result == null) {
                this.validationError = "";
                return true;
            }

            this.validationError = typeof result === "string" ? result : "Invalid value.";
            return false;
        },
        async saveChanges() {
            const trimmed = this.inputValue.trim();
            if (trimmed === this.originalValue) {
                this.revertChanges();
                return;
            }

            if (!this.validateValue(trimmed)) {
                return;
            }

            this.isSaving = true;
            try {
                if (this.saveHandler) {
                    await this.saveHandler(trimmed);
                }
                this.originalValue = trimmed;
                this.inputValue = trimmed;
                this.$emit("update:modelValue", trimmed);
                this.isEditing = false;
            } catch (error) {
                const fallbackMessage = error?.message || "Something went wrong. Please try again.";
                this.validationError = fallbackMessage;
            } finally {
                this.isSaving = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.inline-edit-wrapper {
    --borderColor: var(--asphalt40);
    --hoverBorderColor: var(--asphalt50);
    --editableBorderColor: var(--asphalt100);
    --textColor: var(--asphalt80);
    --editableTextColor: var(--asphalt100);
    --mutedTextColor: var(--asphalt40);
    --errorColor: var(--rose50);

    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    position: relative;

    &.dark {
        --borderColor: var(--asphalt80);
        --hoverBorderColor: var(--asphalt50);
        --editableBorderColor: var(--asphalt10);
        --textColor: var(--asphalt50);
        --editableTextColor: var(--asphalt10);
        --mutedTextColor: var(--asphalt40);
    }

    .inline-edit-input {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 8px 8px 12px;
        border-radius: 6px;
        border: 1px solid var(--borderColor);
        color: var(--textColor);

        &.always-editable {
            &:hover:not(.focused):not(.disabled) {
                border-color: var(--hoverBorderColor);
            }
        }

        &.editable,
        &.always-editable.focused {
            border-color: var(--editableBorderColor);

            input {
                color: var(--editableTextColor);
            }
        }

        &.error {
            border: 1px solid var(--errorColor);
        }

        .input-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-width: 0;
            position: relative;

            .text-viewport {
                flex: 1;
                min-width: 0;
                overflow: hidden;
            }

            .text-track {
                display: flex;
                flex-flow: row nowrap;
                align-items: center;
                min-width: 100%;
            }

            .overflow-marker {
                position: absolute;
                z-index: 2;
                left: 28px;
                top: 50%;
                width: 1px;
                height: 20px;
                background: var(--textColor);
                pointer-events: none;
                transform: translateY(-50%);
            }
        }

        .leading-icon {
            color: var(--mutedTextColor);
            width: 20px;
            height: 20px;
        }

        .prefix-text {
            font: var(--editor-ui-b2-regular);
            white-space: nowrap;
        }

        input {
            border: none;
            background: transparent;
            color: var(--textColor);
            font: var(--editor-ui-b2-regular);
            outline: none;
            width: 100%;
            min-width: 0;
            margin: 0;
        }

        &.always-editable.focused {
            .text-viewport {
                overflow-x: auto;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    display: none;
                }
            }

            .text-track {
                width: max-content;
            }

            input {
                flex: 0 0 auto;
                width: auto;
            }
        }

        .action-button {
            flex: 0 0 auto;

            svg {
                width: 18px;
                height: 18px;
            }
        }
    }

    .assist-text {
        font: var(--editor-ui-b3-regular);
        color: var(--errorColor);
    }

.char-limit-message {
    font: var(--editor-ui-b2-regular);
    display: flex;
    align-items: center;
    color: var(--mutedTextColor);
    opacity: 0;
    transition: opacity 0.15s ease;

    &.outside {
        position: absolute;
        right: -2px;
            bottom: -36px;
            z-index: 1;
            pointer-events: none;
            background: var(--fabBackgroundColor);
            color: var(--fabColor);
            padding: 4px 8px;
            border-radius: 4px;
        }

        &.limit-reached {
            color: var(--errorColor);

            &.outside {
                background: var(--fabAlertColor);
                color: var(--fabColor);
            }
        }
    }
}

.inline-edit-wrapper:focus-within .char-limit-message {
    opacity: 1;
}
</style>
